import React, { useEffect, useState } from 'react';
import {
	Button,
	ListTable,
	Pager,
	Select,
	Typography,
} from 'design_system/src';
import { ReactComponent as AddCircle } from 'design_system/src/static/icons/add-circle.svg';
import { ReactComponent as Edit } from 'design_system/src/static/icons/edit-ui.svg';
import { fetchWhitelist } from 'models/whitelist/controllers/whitelist.controller';
import {
	WhitelistEntry,
	WhitelistNameEnum,
	WhitelistNameValues,
	WhitelistTypeEnum,
	WhitelistTypeValues,
} from 'models/whitelist/interfaces/whitelist.interface';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import SearchBar from 'components/hocs/searchBar/searchBar';
import { formatDate, formatHour } from 'models/shared/helpers/dates';
import CreateWhitelistModal from './CreateWhitelistModal';
import EditWhitelistModal from './EditWhitelistModal';

const vigenteOptions = [
	{ value: '', label: 'Vigencia' },
	{ value: 'true', label: 'Vigentes' },
	{ value: 'false', label: 'Vencidas' },
];

const activeFilterOptions = [
	{ value: '', label: 'Estado' },
	{ value: 'true', label: 'Activos' },
	{ value: 'false', label: 'Inactivos' },
];

const typeFilterOptions = [
	{ value: '', label: 'Tipo' },
	...Object.values(WhitelistTypeEnum).map((value) => ({
		value,
		label: WhitelistTypeValues[value],
	})),
];

const nameFilterOptions = [
	{ value: '', label: 'Nombre' },
	...Object.values(WhitelistNameEnum).map((value) => ({
		value,
		label: WhitelistNameValues[value],
	})),
];

const sortOptions = [
	{ value: '-updatedAt', label: 'Actualización: más reciente' },
	{ value: 'updatedAt', label: 'Actualización: más antigua' },
	{ value: '-createdAt', label: 'Creación: más reciente' },
	{ value: 'createdAt', label: 'Creación: más antigua' },
	{ value: 'expiresAt', label: 'Expiración: más próxima' },
	{ value: '-expiresAt', label: 'Expiración: más lejana' },
];

const Whitelists = () => {
	const [data, setData] = useState<WhitelistEntry[]>([]);
	const [loading, setLoading] = useState(false);
	const [query, setQuery] = useState('');
	const [valueFilter, setValueFilter] = useState('');
	const [nameFilter, setNameFilter] = useState('');
	const [typeFilter, setTypeFilter] = useState('');
	const [vigenteFilter, setVigenteFilter] = useState('');
	const [activeFilter, setActiveFilter] = useState('');
	const [openCreate, setOpenCreate] = useState(false);
	const [editingEntry, setEditingEntry] = useState<WhitelistEntry | null>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [count, setCount] = useState(0);
	const [sort, setSort] = useState('-updatedAt');

	const fetchList = () => {
		setLoading(true);
		fetchWhitelist({
			value: valueFilter || undefined,
			name: (nameFilter as WhitelistNameEnum) || undefined,
			type: (typeFilter as WhitelistTypeEnum) || undefined,
			vigente: vigenteFilter === '' ? undefined : vigenteFilter === 'true',
			isActive: activeFilter === '' ? undefined : activeFilter === 'true',
			page: currentPage,
			limit: 30,
			sort,
		})
			.then((result) => {
				setData(result.data);
				setTotalPages(result.pages);
				setCount(result.count);
			})
			.catch(commonErrorHandler)
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		fetchList();
	}, [currentPage]);

	useEffect(() => {
		if (currentPage === 1) {
			fetchList();
		} else {
			setCurrentPage(1);
		}
	}, [valueFilter, nameFilter, typeFilter, vigenteFilter, activeFilter, sort]);

	const handleSearch = (event: React.FormEvent) => {
		event.preventDefault();
		setValueFilter(query);
	};

	const isVigente = (expiresAt: string | null) =>
		!expiresAt || new Date(expiresAt).getTime() > Date.now();

	return (
		<div className="dim_100_per flex_content_col shadow_hard inner_container bg_neutral_400">
			<div className="flex_header">
				<div className="bg_neutral_0 shadow_hard p_t_xs p_b_md p_x_lg">
					<div className="display_flex flex_gap_sm flex_align_center">
						<Typography scale="heading3" weight="600" textColor="neutral_900">
							Listas blancas
						</Typography>
						<SearchBar
							className="flex_grow_1"
							handleSearch={handleSearch}
							query={query}
							setQuery={setQuery}
						/>
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Ordenar por"
							options={sortOptions}
							value={sort}
							onChange={(e: { target: { value: string } }) =>
								setSort(e.target.value)
							}
						/>
					</div>
					<div className="display_flex flex_gap_xl">
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Nombre"
							options={nameFilterOptions}
							value={nameFilter}
							onChange={(e: { target: { value: string } }) =>
								setNameFilter(e.target.value)
							}
						/>
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Tipo"
							options={typeFilterOptions}
							value={typeFilter}
							onChange={(e: { target: { value: string } }) =>
								setTypeFilter(e.target.value)
							}
						/>
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Vigencia"
							options={vigenteOptions}
							value={vigenteFilter}
							onChange={(e: { target: { value: string } }) =>
								setVigenteFilter(e.target.value)
							}
						/>
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Estado"
							options={activeFilterOptions}
							value={activeFilter}
							onChange={(e: { target: { value: string } }) =>
								setActiveFilter(e.target.value)
							}
						/>
					</div>
				</div>
			</div>
			<div className="flex_body p_x_lg overflow_x_hidden">
				{loading && (
					<Typography scale="medium" weight="400" className="p_y_lg">
						Cargando listas blancas...
					</Typography>
				)}
				{!loading && data.length > 0 && (
					<ListTable
						data={data}
						hideHeader={false}
						canSelect={false}
						className="m_t_md"
						cols={[
							{
								label: 'Nombre',
								render: (row: WhitelistEntry) => (
									<Typography
										scale="medium"
										weight="400"
										className="text_center"
									>
										{WhitelistNameValues[row.name]}
									</Typography>
								),
							},
							{
								label: 'Tipo',
								render: (row: WhitelistEntry) => (
									<Typography
										scale="medium"
										weight="400"
										className="text_center"
									>
										{WhitelistTypeValues[row.type]}
									</Typography>
								),
							},
							{
								label: 'Valor',
								render: (row: WhitelistEntry) => (
									<Typography
										scale="medium"
										weight="400"
										className="text_center"
									>
										{row.value}
									</Typography>
								),
							},
							{
								label: 'Estado',
								render: (row: WhitelistEntry) => (
									<Typography
										scale="xsmall"
										weight="600"
										className={`p_x_md p_y_xxs br_xs text_center text_neutral_0" ${
											row.isActive ? 'bg_primary_300' : 'bg_neutral_600'
										}`}
									>
										{row.isActive ? 'Activo' : 'Inactivo'}
									</Typography>
								),
							},
							{
								label: 'Vigencia',
								render: (row: WhitelistEntry) => (
									<Typography
										scale="xsmall"
										weight="600"
										className={`p_x_md p_y_xxs br_xs text_center text_neutral_0" ${
											isVigente(row.expiresAt)
												? 'bg_primary_300'
												: 'bg_neutral_600'
										}`}
									>
										{isVigente(row.expiresAt) ? 'Vigente' : 'Vencida'}
									</Typography>
								),
							},
							{
								label: 'Expira',
								render: (row: WhitelistEntry) => (
									<Typography
										scale="medium"
										weight="400"
										className="text_center"
									>
										{row.expiresAt ? formatDate(row.expiresAt) : 'Nunca'}
									</Typography>
								),
							},
							{
								label: 'Actualizado',
								render: (row: WhitelistEntry) => (
									<Typography
										scale="medium"
										weight="400"
										className="text_center"
									>
										{formatDate(row.updatedAt)} {formatHour(row.updatedAt)}
									</Typography>
								),
							},
							{
								label: 'Creado',
								render: (row: WhitelistEntry) => (
									<Typography
										scale="medium"
										weight="400"
										className="text_center"
									>
										{formatDate(row.createdAt)} {formatHour(row.createdAt)}
									</Typography>
								),
							},
							{
								label: '',
								render: (row: WhitelistEntry) => (
									<Button
										scale="small"
										variant="ghost"
										icon={<Edit />}
										onClick={() => setEditingEntry(row)}
									>
										Editar
									</Button>
								),
							},
						]}
					/>
				)}
				{!loading && data.length === 0 && (
					<Typography scale="medium" weight="400" className="p_y_lg">
						No hay valores para mostrar
					</Typography>
				)}
			</div>
			<div className="display_flex flex_align_center flex_justify_between flex_footer bg_neutral_0 p_x_xxxl p_y_md">
				<Button
					scale="small"
					icon={<AddCircle />}
					className="w_30_per"
					onClick={() => setOpenCreate(true)}
				>
					Agregar
				</Button>
				<Pager
					totalPages={totalPages}
					setPage={setCurrentPage}
					page={currentPage}
					count={count}
					className="w_fit"
				/>
			</div>
			<CreateWhitelistModal
				open={openCreate}
				setOpen={setOpenCreate}
				onCreated={fetchList}
			/>
			<EditWhitelistModal
				open={!!editingEntry}
				setOpen={(open) => {
					if (!open) setEditingEntry(null);
				}}
				entry={editingEntry}
				onUpdated={fetchList}
			/>
		</div>
	);
};

export default Whitelists;
